import { ImageOff, Loader, ScanBarcode, WifiOff, X } from 'lucide-react'
import { useState, type RefObject } from 'react'

import { Button, Highlight } from '@krp/ui-kit'

import type { PickerRowVm, SearchVm } from '../port/types'

/**
 * Строка поиска РМК — как в старом: одна строка с автофокусом «Штрихкод или
 * наименование — сканируйте или введите»; Enter — первый найденный в чек; Esc —
 * очистить; сканер-клавиатура печатает сюда же и жмёт Enter. Выдача выпадает
 * списком поверх чека и живёт, пока строка держит фокус. Ищет служба по всему
 * снимку каталога кассы (UI-RMK); рядом — «☰ Каталог» и «Сканер» (камера).
 */
export function SearchBar({
  search,
  locked,
  inputRef,
  onChange,
  onSubmit,
  onPick,
  onCatalog,
  onScanner,
  onCancelLookup,
  compact = false,
}: {
  search: SearchVm
  locked: boolean
  inputRef: RefObject<HTMLInputElement | null>
  onChange: (text: string) => void
  onSubmit: () => void
  onPick: (row: PickerRowVm) => void
  onCatalog: () => void
  onScanner: () => void
  /** Снять ожидающий поиск из очереди; нет — у источника очереди поиска нет. */
  onCancelLookup?: (id: string) => void
  /** Телефон: короткая подсказка — длинная обрезалась на полуслове («Штрихкод или наимен…»). */
  compact?: boolean
}) {
  const [focused, setFocused] = useState(true)
  const open = focused && search.status !== 'idle'
  return (
    <div className="rmk-search-area">
      <div className="rmk-search">
        <Button disabled={locked} onClick={onCatalog}>
          ☰ Каталог
        </Button>
        <div className="rmk-search__field">
          <input
            ref={inputRef}
            className="rmk-search__input"
            type="text"
            inputMode="search"
            autoComplete="off"
            // Строка — главное место ввода кассира: сканер-клавиатура печатает сюда же (как в старом РМК).
            autoFocus
            value={search.text}
            placeholder={compact ? 'Код или название' : 'Штрихкод или наименование — сканируйте или введите'}
            aria-label="Поиск товара"
            disabled={locked}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onChange={(event) => onChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape' && search.text !== '') {
                event.preventDefault()
                event.stopPropagation()
                onChange('')
                return
              }
              if (event.key !== 'Enter') return
              event.preventDefault()
              onSubmit()
            }}
          />
          {search.text !== '' && (
            <button
              type="button"
              className="rmk-search__clear"
              aria-label="Очистить поиск"
              title="Очистить поиск"
              // Фокус остаётся в строке: следующий скан обязан попасть туда же.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => onChange('')}
            >
              <X size={16} aria-hidden="true" />
            </button>
          )}
        </div>
        <Button icon={<ScanBarcode size={16} aria-hidden="true" />} disabled={locked} onClick={onScanner}>
          <span className="rmk-search__scanner-text">Сканер</span>
        </Button>
      </div>
      {(search.pending ?? []).length > 0 && (
        // Код со сканера, который не получил ответа, не пропал: он ждёт в очереди и добавится в чек сам, когда связь вернётся.
        <ul className="rmk-search__pending" aria-label="Ожидающие поиски товара">
          {(search.pending ?? []).map((item) => (
            <li key={item.id} role="status">
              <WifiOff size={14} aria-hidden="true" /> <b>{item.text}</b> · {item.statusText}{' '}
              {onCancelLookup !== undefined && (
                <Button variant="ghost" aria-label={`Снять поиск: ${item.text}`} onClick={() => onCancelLookup(item.id)}>
                  Снять
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {open && (
        <ul
          className="rmk-found"
          aria-label="Результаты поиска товаров"
          // Без этого щелчок мышью сначала снимал фокус со строки, выдача исчезала — и нажатие уходило в пустоту.
          onMouseDown={(event) => event.preventDefault()}
        >
          {search.status === 'waiting' && (
            <li className="rmk-found__state" role="status">
              <Loader size={16} aria-hidden="true" /> Ищем товар…
            </li>
          )}
          {search.status === 'retrying' && (
            <li className="rmk-found__state" role="status">
              <WifiOff size={16} aria-hidden="true" /> {search.errorText}
            </li>
          )}
          {search.status === 'error' && (
            <li className="rmk-found__state" role="alert">
              Поиск недоступен: {search.errorText}
            </li>
          )}
          {search.status === 'ready' && search.rows.length === 0 && (
            <li className="rmk-found__state">Ничего не найдено — проверьте штрихкод или наименование</li>
          )}
          {search.rows.map((row) => (
            <li key={row.productKey} className="rmk-found__row" data-available={row.available}>
              <span className="rmk-thumb rmk-thumb--sm" aria-hidden="true">
                <ImageOff size={14} />
              </span>
              <button
                type="button"
                className="rmk-found__item"
                disabled={locked || (!row.available && row.absent == null)}
                aria-label={row.absent == null ? `В чек: ${row.name}` : `Ещё не в кассе: ${row.name}`}
                onClick={() => onPick(row)}
              >
                <span className="rmk-found__name">
                  <span className="rmk-found__title">
                    <Highlight text={row.name} query={search.text} />
                  </span>
                  {row.absent != null ? (
                    <small className="rmk-found__reason">ещё не в кассе: {row.absent.reason} — нажмите, чтобы отправить</small>
                  ) : (
                    row.reason !== null && <small className="rmk-found__reason">нельзя продать: {row.reason}</small>
                  )}
                </span>
                <span className="rmk-found__price">
                  <span className="rmk-found__amount">{row.priceText.replace(/₽$/, '')}</span>
                  {row.priceText.endsWith('₽') && '₽'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
